import axios from ".";

export interface JobData {
  id: string;
  job_type: string;
  status:
    | "pending"
    | "validating"
    | "processing"
    | "completed"
    | "failed"
    | "cancelled";
  filename: string;
  file_size?: number;
  results?: {
    total?: number;
    processed?: number;
    created?: number;
    updated?: number;
    exported?: number;
    errors?: number;
  };
  progress?: {
    total: number;
    processed: number;
    created?: number;
    updated?: number;
    errors?: number;
    message?: string;
  };
  filter_context?: {
    query?: string;
    brand_ids?: string[];
    status_filter?: string;
    original_language_filter?: string;
    used_phrases_only?: boolean;
    suitability_filter?: string;
  };
  errors?: {
    errors: Array<{
      error: string;
      timestamp?: string;
      row?: number;
      data?: unknown;
    }>;
  };
  created_at: string;
  completed_at?: string;
}

// Export request data
export interface ExportRequest {
  query?: string;
  brand_ids?: string[];
  status_filter?: string;
  original_language_filter?: string;
  used_phrases_only?: boolean;
  suitability_filter?: string;
}

// Validation types
export interface ValidationError {
  type: string;
  message: string;
  rows: string;
}

export interface ValidationResult {
  success: boolean;
  total_rows?: number;
  message?: string;
  validation_errors?: ValidationError[];
  error?: string;
}

/**
 * Check for active import/export jobs
 */
export const getActiveJob = async (): Promise<JobData | null> => {
  const response = await axios.get("/import-export/jobs/active");
  return response.data.data;
};

/**
 * Get detailed status of a specific job
 */
export const getJobStatus = async (jobId: string): Promise<JobData> => {
  const response = await axios.get(`/import-export/jobs/${jobId}/status`);
  return response.data.data;
};

/**
 * Get job history for the current user
 */
export const getJobHistory = async (limit: number = 6): Promise<JobData[]> => {
  const response = await axios.get(
    `/import-export/jobs/history?limit=${limit}`
  );
  return response.data.data || [];
};

/**
 * Start a new export job
 */
export const startExportJob = async (
  exportRequest: ExportRequest
): Promise<{
  job_id: string;
  task_id: string;
  message: string;
}> => {
  const response = await axios.post("/import-export/export", exportRequest);
  return response.data.data;
};

/**
 * Start a new import job
 */
export const startImportJob = async (
  file: File
): Promise<{
  job_id: string;
  task_id: string;
  message: string;
}> => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axios.post("/import-export/import", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data.data;
};

/**
 * Cancel an active job
 */
export const cancelJob = async (
  jobId: string
): Promise<{
  id: string;
  status: string;
}> => {
  const response = await axios.post(`/import-export/jobs/${jobId}/cancel`);
  return response.data.data;
};

/**
 * Download job result file
 */
export const downloadJobResult = async (
  jobId: string
): Promise<{
  download_url: string;
  filename: string;
}> => {
  const response = await axios.get(`/import-export/jobs/${jobId}/download`);
  return response.data.data;
};

/**
 * Validate CSV file for import
 */
export const validateCSVFile = async (
  file: File
): Promise<ValidationResult> => {
  const formData = new FormData();
  formData.append("file", file);

  try {
    const response = await axios.post(
      "/import-export/import/validate",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.data;
  } catch (error: unknown) {
    const axiosError = error as {
      response?: {
        data?: {
          data?: ValidationResult;
          message?: string;
        };
        status?: number;
      };
    };

    // If it's a 400 error with validation data, return the validation result
    if (
      axiosError?.response?.status === 400 &&
      axiosError?.response?.data?.data
    ) {
      return axiosError.response.data.data;
    }

    // Otherwise, throw the error to be handled by the calling function
    throw error;
  }
};

/**
 * Basic client-side file validation
 */
export const validateFileBasic = (
  file: File
): { valid: boolean; error?: string } => {
  // Check file type
  if (!file.name.toLowerCase().endsWith(".csv")) {
    return { valid: false, error: "Only CSV files are allowed" };
  }

  // Check file size (max 50MB)
  const maxSizeBytes = 50 * 1024 * 1024; // 50MB
  if (file.size > maxSizeBytes) {
    return { valid: false, error: "File size must be less than 50MB" };
  }

  // Check if file is empty
  if (file.size === 0) {
    return { valid: false, error: "File cannot be empty" };
  }

  return { valid: true };
};

/**
 * Create a download link and trigger download
 */
export const triggerFileDownload = (url: string, filename: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
